import React, { Suspense } from 'react';
import styled from 'styled-components';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 64px; // Header height
  min-height: calc(100vh - 64px - 60px); // Subtract header and footer heights
  
  @media (max-width: 768px) {
    margin-top: 56px; // Smaller header on mobile
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: ${({ $sidebarState, $isMobile }) => {
    if ($isMobile) return '0';
    if ($sidebarState === 'expanded') return '240px';
    if ($sidebarState === 'collapsed') return '68px';
    return '0'; // hidden state
  }};
  
  @media (max-width: 768px) {
    margin-left: 0 !important;
    width: 100%;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;
  
  // Add subtle shadow to separate from sidebars
  box-shadow: ${({ $isWideLayout }) => $isWideLayout ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'};
  
  @media (max-width: 768px) {
    box-shadow: none;
  }
`;

const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MobileBackdrop = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${({ $show }) => $show ? 'block' : 'none'};
  
  @media (max-width: 768px) {
    top: 56px;
  }
`;

const MainLayout = ({
  sidebarState,
  toggleSidebar,
  user,
  onLoginClick,
  onLogout
}) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(false);
  
  // Check screen size
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Define pages that need wider layout
  const wideLayoutPages = [
    '/compare', '/inspector', '/schema-validator', '/fake-api', '/api-tester',
    '/visualization', '/validator', '/viewer', '/visualizer', '/converter',
    '/query', '/masker','/encoder','/generator', '/hosting','/json-score','/security-scanner','/code-generator', '/schema'
  ];
  
  const isWideLayout = wideLayoutPages.includes(location.pathname);
  
  // Close sidebar when clicking on mobile backdrop
  const handleBackdropClick = () => {
    if (isMobile && sidebarState !== 'hidden') {
      toggleSidebar('hidden');
    }
  };

  return (
    <MainContainer>
      <Header
        toggleSidebar={toggleSidebar}
        sidebarState={sidebarState}
        showSidebarToggle={true}
        user={user}
        onLoginClick={onLoginClick}
        onLogout={onLogout}
        isMobile={isMobile}
      />
      
      <ContentWrapper>
        <MobileBackdrop 
          $show={isMobile && sidebarState !== 'hidden'} 
          onClick={handleBackdropClick}
        />
        
        <Sidebar 
          sidebarState={sidebarState} 
          onToggleSidebar={toggleSidebar}
          isMobile={isMobile}
        />
        
        <MainContent 
          $sidebarState={sidebarState} 
          $isMobile={isMobile}
        >
          <ContentArea $isWideLayout={isWideLayout}>
            <Suspense fallback={<LoadingFallback>Loading...</LoadingFallback>}>
              <Outlet />
            </Suspense>
          </ContentArea>
        </MainContent>
      </ContentWrapper>
      
      <Footer />
    </MainContainer>
  );
};

export default MainLayout;